"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, type Variants } from "motion/react";
import { Search, MessageCircle, Mail, Phone, ChevronDown, ChevronRight, Activity, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const faqs = [
    {
        question: "Is Restaubee really free?",
        answer: "Yes. Our core POS, billing, and management features are 100% free with no monthly subscription fees for individual outlets.",
    },
    {
        question: "Does it work without internet?",
        answer: "Restaubee is offline-first. Your billing never stops. Data syncs automatically once you go back online.",
    },
    {
        question: "Can I use it on my existing hardware?",
        answer: "Our platform is cross-platform. It works on Windows, Android, and iOS. If you have a browser or an iPad, you're ready.",
    },
    {
        question: "How do I migrate from my current POS?",
        answer: "We offer a 'Smart Import' tool. Simply upload your menu CSV or Excel sheet, and our AI will categorize everything for you.",
    },
    {
        question: "Is my data secure?",
        answer: "We use bank-grade encryption (AES-256) and automated daily backups on the cloud so your data is never lost.",
    },
    {
        question: "Can I manage multiple outlets?",
        answer: "Unlimited. One account can manage one restaurant or a hundred. No extra charge per branch.",
    },
];

const contacts = [
    { icon: MessageCircle, title: "Live Chat", desc: "Instantly chat with our team", action: "Start Chat", href: "#" },
    { icon: Mail, title: "Email Us", desc: "Response within 24 hours", action: "admin@doodledeveloper.com", href: "mailto:admin@doodledeveloper.com" },
    { icon: Phone, title: "Call Us", desc: "Mon–Sat, 9am–9pm IST", action: "8221955036", href: "tel:8221955036" },
];

export function SupportContent() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    return (
        <main className="relative min-h-screen pt-36 pb-24 px-4 overflow-hidden bg-background text-foreground transition-colors duration-500 perspective-[1500px]">
            {!mounted ? null : (
                <>
                    {/* Background Layering — Matching Architecture */}
                    <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
                        <div className="absolute inset-0 grid-pattern opacity-[0.3] dark:opacity-[0.15]" />
                        
                        {/* Architectural Blueprint Lines */}
                        <div className="absolute top-0 left-[20%] w-px h-full bg-border/40" />
                        <div className="absolute top-0 left-[40%] w-px h-full bg-border/40" />
                        <div className="absolute top-0 left-[60%] w-px h-full bg-border/40" />
                        <div className="absolute top-0 left-[80%] w-px h-full bg-border/40" />
                        
                        {/* Blueprint Labels */}
                        <div className="absolute top-4 left-4 text-[10px] text-muted-foreground font-mono tracking-widest uppercase">Support.Arch // v1.0</div>
                        <div className="absolute bottom-4 right-4 text-[10px] text-muted-foreground font-mono tracking-widest uppercase md:block hidden">System.Support_Matrix</div>
                        
                        {/* Gradient Fades */}
                        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background/90" />
                    </div>

                    <div className="relative z-10 max-w-7xl mx-auto">
                        {/* Technical Badge */}
                        <div className="flex justify-center mb-10">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center gap-3 px-3 py-1 rounded-lg bg-surface border border-border-subtle shadow-sm text-xs font-mono tracking-tighter group cursor-default"
                            >
                                <div className="flex gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                                </div>
                                <span className="text-muted-foreground">SUPPORT_NODE:</span>
                                <span className="text-primary font-bold">ONLINE</span>
                            </motion.div>
                        </div>

                        {/* Headings */}
                        <div className="text-center mb-20">
                            <h1 className="text-5xl md:text-8xl font-semibold tracking-tight text-foreground mb-8 leading-[0.9] flex flex-col items-center">
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    How can we
                                </motion.span>
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                                    className="text-gradient font-bold"
                                >
                                    help you?
                                </motion.span>
                            </h1>

                            {/* Search Bar — Sleek Glassmorphism */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2 }}
                                className="relative max-w-xl mx-auto group z-20"
                            >
                                <div className="absolute inset-0 bg-primary/5 blur-xl group-focus-within:bg-primary/10 transition-colors" />
                                <div className="relative flex items-center p-2 rounded-2xl border border-border-subtle bg-surface/80 backdrop-blur-xl shadow-sm transition-all focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20">
                                    <div className="p-3">
                                        <Search size={20} className="text-muted-foreground" />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Search for documentation, tutorials..."
                                        className="flex-1 bg-transparent border-none outline-none py-3 text-sm font-medium text-left text-foreground placeholder:text-muted-foreground/60 w-full"
                                    />
                                    <div className="hidden md:block px-4 py-2 bg-muted/50 rounded-xl border border-border text-[10px] font-bold text-muted-foreground mr-2 uppercase tracking-widest cursor-default">
                                        ⌘ + K
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Contact Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-32 max-w-5xl mx-auto">
                            {contacts.map((c, i) => (
                                <a href={c.href} key={i}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 + i * 0.1 }}
                                        className="p-8 rounded-[2rem] border border-border-subtle bg-surface/50 backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.03)] dark:shadow-none transition-all hover:bg-surface hover:border-border group cursor-pointer h-full flex flex-col justify-between"
                                    >
                                        <div>
                                            <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform shadow-inner">
                                                <c.icon size={22} strokeWidth={2.5} />
                                            </div>
                                            <h3 className="font-semibold tracking-tight text-xl mb-2 text-foreground">{c.title}</h3>
                                            <p className="text-sm text-muted-foreground mb-8">{c.desc}</p>
                                        </div>
                                        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary flex items-center gap-2 group-hover:translate-x-1 transition-transform">
                                            {c.action} <ChevronRight size={14} />
                                        </div>
                                    </motion.div>
                                </a>
                            ))}
                        </div>

                        {/* Frequently Asked Questions */}
                        <div className="max-w-4xl mx-auto mb-32 relative z-10">
                            <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-4">
                                <div>
                                    <h2 className="text-4xl font-semibold tracking-tight mb-2 text-foreground flex items-center gap-3">
                                        <Activity className="text-primary" size={28} /> Knowledge Base
                                    </h2>
                                    <p className="text-base text-muted-foreground">Quick solutions to common integration questions.</p>
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground cursor-pointer transition-colors border-b border-border pb-1">
                                    View Full Documentation →
                                </div>
                            </div>

                            <div className="space-y-4">
                                {faqs.map((faq, i) => (
                                    <div
                                        key={i}
                                        className={cn(
                                            "rounded-2xl border transition-all duration-300 overflow-hidden backdrop-blur-sm", 
                                            openIndex === i ? "bg-surface border-border shadow-sm ring-1 ring-primary/5" : "bg-surface/30 border-border-subtle hover:bg-surface/50"
                                        )}
                                    >
                                        <button
                                            onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                            className="w-full flex items-center justify-between p-6 md:p-8 text-left transition-colors"
                                        >
                                            <span className="text-lg md:text-xl font-medium tracking-tight text-foreground pr-8">{faq.question}</span>
                                            {openIndex === i 
                                                ? <ChevronDown size={24} className="text-primary shrink-0 transition-transform" /> 
                                                : <ChevronRight size={24} className="text-muted-foreground/40 shrink-0 transition-transform" />
                                            }
                                        </button>

                                        <AnimatePresence>
                                            {openIndex === i && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-6 md:px-8 pb-8 text-base text-muted-foreground leading-relaxed">
                                                        <div className="p-4 rounded-xl bg-background/50 border border-border-subtle">
                                                            {faq.answer}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer Banner */}
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-primary/20 p-[1px] rounded-[2.5rem] max-w-4xl mx-auto"
                        >
                            <div className="bg-surface/80 backdrop-blur-xl rounded-[2.5rem] p-10 md:p-16 text-center border border-border-subtle shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)]">
                                <h3 className="text-3xl md:text-5xl font-semibold mb-6 tracking-tight text-foreground">
                                    Still need <span className="text-gradient font-bold">assistance?</span>
                                </h3>
                                <p className="text-muted-foreground text-base md:text-lg mb-10 max-w-md mx-auto leading-relaxed">
                                    Our dedicated enterprise support team is available around the clock to ensure your operations never stop.
                                </p>
                                <button className="px-10 py-5 bg-foreground text-background rounded-2xl font-bold text-sm tracking-tight hover:scale-[1.02] transition-all shadow-md flex items-center justify-center gap-2 mx-auto">
                                    Join Core Community <ArrowRight size={16} />
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </main>
    );
}
